import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import useAuthStore from '@/stores/auth'
import useConfigStore from '@/stores/config'
import useNotificationsStore from '@/stores/notifications'
import useModalStore from '@/stores/modal'
import userHttp from '@/api/user'
import { type BalanceData, type UserData } from '@/types/user'

const useUserStore = defineStore('user', () => {
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const notificationsStore = useNotificationsStore()
  const modalStore = useModalStore()

  const { handleErrorNotification } = notificationsStore

  const userData = ref<UserData | null>(null)
  const balanceData = ref<BalanceData | null>(null)
  const isCasinoGameOpen = ref(false)

  const isBalanceBelowThreshold = computed(
    () =>
      authStore.isAuthenticated &&
      (userData.value?.balance ?? 0) < (configStore.appSettings?.lowBalance.minBalance ?? 0)
  )

  const getUser = async () => {
    try {
      const response = await userHttp.getUser()
      const { currency, balance } = balanceData.value ?? {}

      userData.value = {
        ...response,
        currency: currency ?? null,
        balance: balance ?? null
      }
    } catch (err) {
      handleErrorNotification(err)
    }
  }

  const getBalance = async () => {
    try {
      const response = await userHttp.getBalance()
      balanceData.value = response

      if (userData.value) {
        const { currency, balance } = response
        userData.value = Object.assign(userData.value, {
          currency,
          balance
        })
      }

      if (isBalanceBelowThreshold.value) {
        modalStore.openModalWithDelay(
          'lowBalance',
          (configStore.appSettings?.lowBalance.openDelay ?? 0) * 1000
        )
      } else {
        modalStore.closeDelayedModal('lowBalance')
      }
    } catch (err) {
      handleErrorNotification(err)
    }
  }

  return {
    userData,
    balanceData,
    isCasinoGameOpen,
    isBalanceBelowThreshold,
    getUser,
    getBalance
  }
})

export default useUserStore
